import type { ApexOptions } from 'apexcharts';

import { useMemo } from 'react';
import ReactApexChart from 'react-apexcharts';

import EmptyState from '@/components/EmptyState';
import { useFormatText } from '@/hooks/useFormatText';
import { useSettingsStore } from '@/store/useSettingsStore';
import { IParentWeeklyActivityItem } from '@/types/parent';

const CHART_FONT_FAMILY = 'Aptos, sans-serif';
const HOURS_THRESHOLD_MIN = 60;

const UNIT_TEXT = {
  short: { min: 'min', hr: 'hr' },
  long: { min: 'Minutes', hr: 'Hours' },
} as const;

const formatTickValue = (value: number, useHours: boolean): string => {
  if (!Number.isFinite(value)) return '0';
  if (!useHours) return `${Math.round(value)}`;
  const rounded = Math.round(value * 10) / 10;
  return rounded === Math.floor(rounded)
    ? rounded.toFixed(0)
    : rounded.toFixed(1);
};

// Pick a "nice" axis max so the 5 ticks fall on round numbers.
const computeNiceYMax = (max: number, fallback: number): number => {
  if (!Number.isFinite(max) || max <= 0) return fallback;
  const rough = max / 5;
  const magnitude = Math.pow(10, Math.floor(Math.log10(rough)));
  const normalized = rough / magnitude;
  let nice: number;
  if (normalized <= 1) nice = 1;
  else if (normalized <= 2) nice = 2;
  else if (normalized <= 5) nice = 5;
  else nice = 10;
  return nice * magnitude * 5;
};

interface IWeeklyActivityChartProps {
  data: IParentWeeklyActivityItem[];
}

const WeeklyActivityChart: React.FC<IWeeklyActivityChartProps> = ({ data }) => {
  const direction = useSettingsStore((s) => s.settings.direction);
  const isRTL = direction === 'rtl';
  const { formatText } = useFormatText();

  const chartData = useMemo(
    () =>
      (data ?? []).map((item) => ({
        label: item?.label ?? '',
        minutes: Math.round((item?.play_time_seconds ?? 0) / 60),
      })),
    [data]
  );

  const hasActivity = chartData.some((item) => (item?.minutes ?? 0) > 0);

  const maxMinutes = useMemo(
    () => chartData.reduce((acc, item) => Math.max(acc, item?.minutes ?? 0), 0),
    [chartData]
  );

  const useHours = maxMinutes >= HOURS_THRESHOLD_MIN;
  const unitKey = useHours ? 'hr' : 'min';
  const unitShort = UNIT_TEXT.short[unitKey];
  const unitLabel = UNIT_TEXT.long[unitKey];

  const seriesData = useMemo(
    () =>
      chartData.map((item) => {
        const minutes = item?.minutes ?? 0;
        return useHours ? Math.round((minutes / 60) * 10) / 10 : minutes;
      }),
    [chartData, useHours]
  );

  const yMax = useMemo(() => {
    if (useHours) return computeNiceYMax(maxMinutes / 60, 5);
    if (maxMinutes <= 0) return 60;
    return Math.ceil(maxMinutes / 30) * 30;
  }, [maxMinutes, useHours]);

  const series = useMemo(
    () => [
      {
        name: unitLabel,
        data: seriesData,
      },
    ],
    [seriesData, unitLabel]
  );

  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        type: 'bar',
        height: 220,
        toolbar: { show: false },
        fontFamily: CHART_FONT_FAMILY,
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: '55%',
          borderRadius: 6,
          borderRadiusApplication: 'end',
          dataLabels: { position: 'top' },
        },
      },
      dataLabels: { enabled: false },
      stroke: {
        show: true,
        width: 2,
        colors: ['transparent'],
      },
      fill: {
        opacity: 1,
        colors: ['#F97316'],
      },
      colors: ['#F97316'],
      xaxis: {
        categories: chartData.map((item) => item?.label ?? ''),
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: {
          style: {
            colors: '#525866',
            fontSize: '12px',
            fontWeight: 400,
            fontFamily: CHART_FONT_FAMILY,
          },
        },
      },
      yaxis: {
        min: 0,
        max: yMax,
        tickAmount: 5,
        opposite: isRTL,
        labels: {
          style: {
            colors: '#525866',
            fontSize: '12px',
            fontWeight: 400,
            fontFamily: CHART_FONT_FAMILY,
          },
          formatter: (value: number) =>
            `${formatTickValue(value, useHours)} ${unitShort}`,
        },
      },
      grid: {
        borderColor: '#E1E4EA',
        strokeDashArray: 0,
        position: 'back',
        xaxis: { lines: { show: false } },
        yaxis: { lines: { show: true } },
        padding: {
          top: 0,
          right: isRTL ? 16 : 0,
          bottom: 0,
          left: isRTL ? 0 : 16,
        },
      },
      tooltip: {
        enabled: true,
        theme: 'light',
        style: { fontSize: '12px', fontFamily: CHART_FONT_FAMILY },
        custom: ({ dataPointIndex }) => {
          const item = chartData[dataPointIndex];
          if (!item) return '';
          const minutes = item.minutes ?? 0;
          const label = item.label ?? '';
          const displayValue = useHours
            ? formatTickValue(minutes / 60, true)
            : `${minutes}`;
          if (isRTL) {
            return `
              <div style="background:white;border:1px solid #f5f7fa;border-radius:5px;box-shadow:0 0 18px rgba(38,38,38,0.2);padding:10px 11px;min-width:110px;direction:rtl;text-align:right;">
                <div style="font-weight:600;font-size:12px;color:#d97706;line-height:20px;margin-bottom:6px;font-family:Aptos,sans-serif;">${label}</div>
                <div style="display:flex;justify-content:space-between;gap:8px;font-family:Aptos,sans-serif;">
                  <span style="font-size:12px;color:#525866;">${unitLabel}</span>
                  <span style="font-weight:600;font-size:12px;color:#181B25;">${displayValue} ${unitShort}</span>
                </div>
              </div>`;
          }
          return `
            <div style="background:white;border:1px solid #f5f7fa;border-radius:5px;box-shadow:0 0 18px rgba(38,38,38,0.2);padding:10px 11px;min-width:110px;">
              <div style="font-weight:600;font-size:12px;color:#d97706;line-height:20px;margin-bottom:6px;font-family:Aptos,sans-serif;">${label}</div>
              <div style="display:flex;justify-content:space-between;gap:8px;font-family:Aptos,sans-serif;">
                <span style="font-size:12px;color:#525866;">${unitLabel}</span>
                <span style="font-weight:600;font-size:12px;color:#181B25;">${displayValue} ${unitShort}</span>
              </div>
            </div>`;
        },
      },
      states: {
        hover: { filter: { type: 'darken', value: 0.85 } },
        active: { filter: { type: 'darken', value: 0.85 } },
      },
    }),
    [chartData, isRTL, yMax, useHours, unitShort, unitLabel]
  );

  if (!hasActivity) {
    return (
      <EmptyState
        imageSrc="/not-found.svg"
        imageAlt="no-data"
        title={formatText('parent-dashboard.weekly-activity.empty-title')}
        description={formatText(
          'parent-dashboard.weekly-activity.empty-description'
        )}
        className="h-auto! min-h-50! gap-3! py-4!"
        titleClassName="text-base!"
        width={120}
        height={120}
      />
    );
  }

  return (
    <div className="w-full">
      <ReactApexChart
        options={options}
        series={series}
        type="bar"
        height={220}
      />
    </div>
  );
};

export default WeeklyActivityChart;
